/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports functions, not a React component. */
import type { RowData } from '@tanstack/react-table';

import type { TreeRow } from './DimeTreeTable.types.js';

interface RowSiblingPosition {
  posinset: number;
  setsize: number;
}

// Root rows have no parentId; group them under a key no real row id can collide with.
const ROOT_GROUP_KEY = '__dime-tree-table-root__';

/**
 * Groups a row list by parentId, preserving each group's relative order.
 *
 * Must be called on `table.getRowModel().rows` (the filtered/visible list), not
 * `table.getCoreRowModel().rows` — `row.getParentRow()?.subRows` resolves through TanStack's
 * unfiltered core model, so building sibling sets from it would report a sibling count/position
 * that ignores an active search filter entirely (see row-sibling-position.test.ts and
 * DimeTreeTable.md's "Search semantics" for why that reads wrong to a screen reader: a filtered-
 * out sibling isn't reachable at all, unlike a paginated/virtualized one, so it shouldn't count).
 */
export function buildSiblingGroups<TData extends RowData>(
  rows: readonly TreeRow<TData>[],
): Map<string, TreeRow<TData>[]> {
  const groups = new Map<string, TreeRow<TData>[]>();
  for (const row of rows) {
    const key = row.parentId ?? ROOT_GROUP_KEY;
    const group = groups.get(key);
    if (group) {
      group.push(row);
    } else {
      groups.set(key, [row]);
    }
  }
  return groups;
}

/**
 * aria-posinset/aria-setsize computed within a row's OWN sibling set — the other rows sharing its
 * parentId in `siblingGroups`, not the flattened, depth-mixed visible row list.
 *
 * This is the substantive difference from DimeNestedTable's NestedTableRow, which uses the flat
 * page index: every row there is level 1, so "flat index" and "sibling index" are the same number.
 * Once rows span arbitrary depth, a node's position has to be stated relative to its siblings —
 * "3rd of 5 categories under Edibles" — not its position in the whole flattened, expanded list.
 */
export function rowSiblingPosition<TData extends RowData>(
  row: TreeRow<TData>,
  siblingGroups: ReadonlyMap<string, readonly TreeRow<TData>[]>,
): RowSiblingPosition {
  const siblings = siblingGroups.get(row.parentId ?? ROOT_GROUP_KEY) ?? [row];
  const posinset = siblings.findIndex((sibling) => sibling.id === row.id) + 1;
  return { posinset, setsize: siblings.length };
}
